/**
 * The one list of hosts `next/image` is allowed to load, plus the predicate that
 * answers "will next/image render this src?".
 *
 * WHY THIS IS SHARED. `next.config.ts` turns this list into `images.remotePatterns`;
 * the chrome resolver (`siteLogos`) and the editor's image picker use the predicate
 * to reject an authored URL BEFORE it reaches `<Image>`. If those ever disagree, a
 * URL passes validation and then fails in the renderer anyway — which is precisely
 * the failure this module exists to prevent. One list, three readers, no drift.
 *
 * TWO EVALUATION TIMES, and they must agree. `next.config.ts` calls this at BUILD
 * time: with `output: "standalone"` the resolved config is serialised into the
 * artifact (`required-server-files.json`, inlined into `server.js`) and the
 * production server never reads `next.config.ts` again — so whatever env the
 * BUILD saw is what the optimizer enforces, forever. The server-side predicate
 * (`siteLogos`, the picker's `hosts`) calls this at REQUEST time with the pod's
 * env. CI deliberately builds with no portal env (one image, any portal), which
 * is why the API host is NOT derived from env alone: the production image used
 * to ship with no API host at all, every api1 image 400'd at the optimizer
 * (roadmap O6, 2026-07-05 → 2026-08-23), and setting the pod's env could never
 * have fixed it. The `**.lecturehub.com/assets/**` entry below is baked for the
 * same reason `imagedelivery.net` is: it is the backend family every portal
 * talks to. The env-derived entry still covers dev's internal hostname and any
 * non-lecturehub backend. Verified in `scripts/smoke-test.sh` (an api-hosted
 * image must come back 200 through the optimizer on the standalone build).
 *
 * The `process.env` reads here never reach the browser bundle: the picker gets
 * the built list as a prop.
 */

/** Just the env vars this module reads — so callers and tests can pass a plain object. */
export type EnvLike = {
  LECTUREHUB_API_URL?: string;
  NEXT_PUBLIC_LECTUREHUB_API_URL?: string;
  /** Proper name; the NEXT_PUBLIC_ spelling is the transitional legacy alias
   *  (this is a BUILD-time read via next.config, so NEXT_PUBLIC_ never meant
   *  anything here — the prefix is for client-bundle inlining). */
  PORTAL_IMAGE_HOSTS?: string;
  NEXT_PUBLIC_PORTAL_IMAGE_HOSTS?: string;
  // Keeps `process.env` assignable: without an index signature TS's weak-type
  // check rejects it, since every named field here is optional.
  [key: string]: string | undefined;
};

/** The subset of Next's `RemotePattern` shape this project actually uses. */
export type ImageRemotePattern = {
  protocol: "https";
  hostname: string;
  /** Only the `…/**` suffix form is used or supported here. */
  pathname?: string;
};

/**
 * Hosts allow-listed for `next/image`, derived per-deploy from runtime env.
 *
 * HTTP is intentionally excluded — no production asset should be plaintext.
 * `imagedelivery.net` is the Cloudflare Images delivery host shared by ALL tenants,
 * so it is baked in rather than pushed to env (see docs/synced/images/README.md).
 *
 * The backend serves file/media assets (the raw `Image.uri` / `Audio.uri` download
 * URLs) from the API host under a portal-in-path scheme, `{API_HOST}/assets/{portalId}/…`
 * (this retired the old per-portal `asset_url`; see docs/synced/portal-api/05-types.md
 * → "Asset & download URLs"). So the API host is derived from the configured API URL —
 * per-fork correct, never hardcoded — and only its `/assets` path is allowed. Display
 * thumbnails still come from Cloudflare Images; this covers the `Image.uri` fallback
 * path and non-CF forks.
 *
 * In `output: "standalone"` the runtime server (`node server.js`) loads next.config at
 * boot, so these `process.env` reads reflect the container's RUNTIME env: the API host
 * follows the deploy without a rebuild, matching the runtime `LECTUREHUB_API_URL` the
 * GraphQL transport uses. The `NEXT_PUBLIC_*` fallback is transitional; see
 * lib/lecturehub/config.ts.
 */
export function imageRemotePatterns(env: EnvLike = process.env): ImageRemotePattern[] {
  const apiAssetHost = (() => {
    try {
      const apiUrl = env.LECTUREHUB_API_URL ?? env.NEXT_PUBLIC_LECTUREHUB_API_URL ?? "";
      return new URL(apiUrl).hostname || null;
    } catch {
      return null;
    }
  })();

  return [
    ...(
      env.PORTAL_IMAGE_HOSTS ??
      env.NEXT_PUBLIC_PORTAL_IMAGE_HOSTS ??
      "assets-akademija.elementum.si"
    )
      .split(",")
      .map((h) => h.trim())
      .filter(Boolean)
      .map((hostname) => ({ protocol: "https" as const, hostname })),
    { protocol: "https" as const, hostname: "imagedelivery.net" },
    // Every LectureHub backend (api1.lecturehub.com today, api2 tomorrow), assets
    // path only. Baked because the production image is built without portal env
    // (see the header) — this is what makes api-hosted images render in prod.
    { protocol: "https" as const, hostname: "**.lecturehub.com", pathname: "/assets/**" },
    // Portal-in-path asset host (raw Image.uri / Audio.uri downloads) for a
    // backend outside that family — dev's docker-internal hostname, a fork on
    // its own domain. Redundant with the entry above on production; harmless.
    ...(apiAssetHost
      ? [{ protocol: "https" as const, hostname: apiAssetHost, pathname: "/assets/**" }]
      : []),
  ];
}

/**
 * Next's `remotePatterns` hostname wildcards: `**.example.com` matches any
 * depth of subdomain (`a.example.com`, `a.b.example.com`) but NOT the apex;
 * `*.example.com` exactly one label. Anything else is an exact match. Kept to
 * those two forms on purpose — a third would be a silent false-accept.
 */
function hostnameMatches(pattern: string, hostname: string): boolean {
  if (pattern.startsWith("**.")) {
    return hostname.endsWith(pattern.slice(2)) && hostname.length > pattern.length - 2;
  }
  if (pattern.startsWith("*.")) {
    const rest = pattern.slice(2);
    return hostname.endsWith(`.${rest}`) && !hostname.slice(0, -rest.length - 1).includes(".");
  }
  return pattern === hostname;
}

function pathnameMatches(pattern: string | undefined, pathname: string): boolean {
  if (!pattern) return true;
  // Only the suffix-glob form is used in this repo. Anything else would be a
  // silent false-accept, so treat it as no match rather than guessing.
  if (!pattern.endsWith("/**")) return pattern === pathname;
  return pathname.startsWith(pattern.slice(0, -2));
}

/**
 * True when `<Image src={src}>` will render.
 *
 * What `next/image` does with an unconfigured host or a malformed URL depends on
 * the build (verified in the installed `image-loader.js`, Next 16.2): under
 * `next dev` the loader THROWS at render time — survivable in a block
 * (`BlockBoundary`), not in the chrome, which renders from a layout and would take
 * every page to the parent `error.tsx`. In a PRODUCTION build the same checks are
 * compiled out; the loader emits `/_next/image?url=…` and the optimizer answers
 * 400, so visitors get a dead <img> — silently, with nothing in Sentry. Either way
 * the URL was never going to show, hence: validate, then fall back (the chrome to
 * the brand wordmark; the picker to an explanation).
 *
 * Accepts local `/public` paths unconditionally (no allow-list applies to them) and
 * rejects everything else that is not an https URL on an allow-listed host — which
 * also disposes of `data:` and `javascript:` srcs.
 */
export function isRenderableImageSrc(
  src: string | null | undefined,
  patterns: ImageRemotePattern[] = imageRemotePatterns(),
): boolean {
  const value = (src ?? "").trim();
  if (!value) return false;

  // Local asset served from public/. `//host/path` is protocol-relative, not local.
  if (value.startsWith("/")) return !value.startsWith("//");

  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }
  if (url.protocol !== "https:") return false;

  return patterns.some(
    (p) => hostnameMatches(p.hostname, url.hostname) && pathnameMatches(p.pathname, url.pathname),
  );
}
